import { makeAutoObservable, computed } from "mobx";
import {
  ConditionsLogicOperator,
  type ActionReference,
  type ConditionsConfig_ConditionConfig,
  type ConditionsConfig_ConditionsConfigGroup,
  type OutputReference,
} from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import { v4 as uuid } from "uuid";
import type {
  ActionCandidate,
  ActionGroupCandidate,
  AutomationCandidate,
  BranchCandidate,
  BranchesActionGroup,
  BranchesActionGroupCandidate,
  ElseBranchCandidate,
} from "../../types/candidate";
import type { CandidateStore } from "./candidate.store";
import type { ActionListMerger } from "./mergers/actions";
import { ejectReferences } from "./utils/filters";
import {
  collectActionsDeep,
  collectInstanceIdsDeep,
  findGroupByInstanceId,
  mapActionsDeep,
  walkActionGroups,
} from "./utils/action-group-walk";
import { featureFlagsStore } from "../feature-flags.store";
import type { AutomationLimits } from "../feature-flags.store";

/**
 * Identifies a single branch (or the else-branch, via the "else" sentinel)
 * inside a specific branches action group.
 */
// eslint-disable-next-line import/no-unused-modules -- consumed by branches.store.test.ts and the upcoming Conditional Logic UI stage
export interface BranchRef {
  instanceId: number;
  branchHash: string | "else";
}

export class BranchesStore {
  private readonly candidateStore: CandidateStore;

  constructor(candidateStore: CandidateStore) {
    this.candidateStore = candidateStore;
    makeAutoObservable(
      this,
      {
        self: computed,
        hasBranches: computed,
      },
      { autoBind: true },
    );
  }

  /** Gets the automation candidate */
  get self(): AutomationCandidate {
    return this.candidateStore.self;
  }

  /** UCES-flagged limits; see AutomationLimits. */
  private get limits(): AutomationLimits {
    return featureFlagsStore.limits;
  }

  /**
   * True if a branches action group exists anywhere in the action-group
   * tree (root level, or nested inside a loop/branches). Drives whether the
   * UI switches into canvas mode.
   */
  get hasBranches(): boolean {
    let found = false;
    walkActionGroups(this.self.actionGroups, (group) => {
      if (group.config.oneofKind === "branches") {
        found = true;
      }
    });
    return found;
  }

  /** Finds a branches block's candidate data anywhere in the tree by its group instanceId */
  findBlock(instanceId: number): BranchesActionGroupCandidate | undefined {
    return this.findBlockGroup(instanceId)?.config.branches;
  }

  /** Finds a single branch (or the else-branch) by ref */
  findBranch(
    ref: BranchRef,
  ): BranchCandidate | ElseBranchCandidate | undefined {
    const block = this.findBlock(ref.instanceId);
    if (block === undefined) return undefined;

    return ref.branchHash === "else"
      ? block.elseBranch
      : block.branches.find((branch) => branch.hash === ref.branchHash);
  }

  /**
   * Actions that live directly in the branch's own "actions" groups.
   * Deliberately shallow: any loop/branches groups nested inside the branch
   * are excluded, so callers always get the branch's own flat action list.
   */
  branchActions(ref: BranchRef): ActionCandidate[] {
    const branch = this.findBranch(ref);
    if (branch === undefined) return [];

    return collectActionsDeep(
      branch.actionGroups.filter(
        (group) => group.config.oneofKind === "actions",
      ),
    );
  }

  /**
   * True when the branch holds any content: actions (deep) or any nested
   * non-actions group (a nested loop/branches counts as content even while
   * empty -- spec: "no actions, no nested content").
   */
  branchHasContent(ref: BranchRef): boolean {
    const branch = this.findBranch(ref);
    if (branch === undefined) return false;

    const hasNestedGroups = branch.actionGroups.some(
      (group) => group.config.oneofKind !== "actions",
    );
    return (
      hasNestedGroups || collectActionsDeep(branch.actionGroups).length > 0
    );
  }

  /**
   * True when the block holds any content in any of its branches or its
   * else-branch (actions deep, or a nested loop/branches group -- consistent
   * with branchHasContent). Drives the block delete flow: an empty block
   * deletes immediately, a block with content asks for confirmation first
   * (spec 10.4).
   */
  blockHasContent(instanceId: number): boolean {
    const block = this.findBlock(instanceId);
    if (block === undefined) return false;

    const refs: BranchRef[] = [
      ...block.branches.map((b) => ({ instanceId, branchHash: b.hash })),
      ...(block.elseBranch !== undefined
        ? [{ instanceId, branchHash: "else" as const }]
        : []),
    ];
    return refs.some((ref) => this.branchHasContent(ref));
  }

  /** Raw condition-count data for a branch; text formatting happens in the UI stage. */
  conditionSummary(ref: BranchRef): "no-conditions" | "else" | number {
    if (ref.branchHash === "else") {
      const block = this.findBlock(ref.instanceId);
      // Missing block, or a block whose else-branch doesn't exist -- treated as empty.
      return block?.elseBranch !== undefined ? "else" : "no-conditions";
    }

    const branch = this.findConditionsBranch(ref);
    if (branch === undefined) return "no-conditions";

    const count = branch.conditions?.group.length ?? 0;
    return count === 0 ? "no-conditions" : count;
  }

  /**
   * Advanced-group ancestry of a branch, split by type and INCLUDING the
   * branch's own block. Callers sum the two counts for the JOINT depth gate
   * (spec 6.2): Conditional Logic and Looping levels count together, so at the
   * default depth of 2 any 3rd Advanced Logic level (CL->Loop->CL,
   * Loop->CL->Loop, ...) is blocked.
   */
  advancedAncestorsOfBranch(ref: BranchRef): {
    branches: number;
    loop: number;
  } {
    const counts = { branches: 0, loop: 0 };
    const found = this.countAncestors(this.self.actionGroups, ref, counts);
    return found ? counts : { branches: 0, loop: 0 };
  }

  /** The branch's single advanced (non-actions) group, if any. */
  branchAdvancedGroup(ref: BranchRef): ActionGroupCandidate | undefined {
    const branch = this.findBranch(ref);
    return branch?.actionGroups.find(
      (group) => group.config.oneofKind !== "actions",
    );
  }

  /**
   * At most ONE advanced group per branch, and at most
   * `maxAdvancedLogicDepth` levels of Advanced Logic on the path, counting
   * Conditional Logic and Looping JOINTLY (spec 6.2).
   * advancedAncestorsOfBranch already includes the branch's own block, so a
   * joint total below the limit means the new nested block still lands within
   * it.
   *
   * All nested adds additionally require the `nested_loops` UCES flag
   * (canAddBlockAt also `conditional_logic`); root-level adds don't go
   * through these gates.
   */
  canAddBlockAt(ref: BranchRef): boolean {
    const { branches, loop } = this.advancedAncestorsOfBranch(ref);
    return (
      featureFlagsStore.nestedLoops &&
      featureFlagsStore.conditionalLogic &&
      this.branchAdvancedGroup(ref) === undefined &&
      branches + loop < this.limits.maxAdvancedLogicDepth &&
      this.findBranch(ref) !== undefined
    );
  }

  canAddLoopAt(ref: BranchRef): boolean {
    const { branches, loop } = this.advancedAncestorsOfBranch(ref);
    return (
      featureFlagsStore.nestedLoops &&
      this.branchAdvancedGroup(ref) === undefined &&
      branches + loop < this.limits.maxAdvancedLogicDepth &&
      this.findBranch(ref) !== undefined
    );
  }

  /** Inserts a default Conditional Logic block into the branch's sequence. */
  addBlockAt(ref: BranchRef): number | undefined {
    if (!this.canAddBlockAt(ref)) return undefined;
    const branch = this.findBranch(ref);
    if (branch === undefined) return undefined;

    const group = this.buildDefaultBlock();
    branch.actionGroups = [...branch.actionGroups, group];
    return group.instanceId;
  }

  /** Inserts an empty Looping group into the branch's sequence. */
  addLoopAt(ref: BranchRef): number | undefined {
    if (!this.canAddLoopAt(ref)) return undefined;
    const branch = this.findBranch(ref);
    if (branch === undefined) return undefined;

    // same shape as mergers/actions/list.ts addLoopActionGroup, built with the
    // store's own id minting so tests don't depend on the global rootStore
    const group = this.buildDefaultLoop();
    branch.actionGroups = [...branch.actionGroups, group];
    return group.instanceId;
  }

  /** The loop's single advanced (non-actions) group, if any. */
  loopAdvancedGroup(instanceId: number): ActionGroupCandidate | undefined {
    const group = findGroupByInstanceId(this.self.actionGroups, instanceId);
    if (group?.config.oneofKind !== "loop") return undefined;
    return group.config.loop.actionGroups.find(
      (nested) => nested.config.oneofKind !== "actions",
    );
  }

  /**
   * Gate for adding an Advanced Logic block (Conditional Logic or Looping)
   * INSIDE a loop: at most one advanced group per loop, and at most
   * `maxAdvancedLogicDepth` levels of Advanced Logic on the path, counting CL
   * and Looping JOINTLY (spec 6.2). countAncestorsOfGroup includes the loop
   * itself, so a joint total below the limit means the new nested block still
   * lands within it.
   */
  private canAddAdvancedInLoop(instanceId: number): boolean {
    if (!featureFlagsStore.nestedLoops) return false;
    const group = findGroupByInstanceId(this.self.actionGroups, instanceId);
    if (group?.config.oneofKind !== "loop") return false;
    if (this.loopAdvancedGroup(instanceId) !== undefined) return false;

    const counts = { branches: 0, loop: 0 };
    const found = this.countAncestorsOfGroup(
      this.self.actionGroups,
      instanceId,
      counts,
    );
    return (
      found && counts.branches + counts.loop < this.limits.maxAdvancedLogicDepth
    );
  }

  /** Gate for adding a CL block inside a loop (see canAddAdvancedInLoop). */
  canAddBlockInLoop(instanceId: number): boolean {
    return (
      featureFlagsStore.conditionalLogic &&
      this.canAddAdvancedInLoop(instanceId)
    );
  }

  /** Gate for adding a nested Looping block inside a loop (spec 6.3). */
  canAddLoopInLoop(instanceId: number): boolean {
    return this.canAddAdvancedInLoop(instanceId);
  }

  /** Inserts a default Conditional Logic block into the loop's sequence. */
  addBlockInLoop(instanceId: number): number | undefined {
    if (!this.canAddBlockInLoop(instanceId)) return undefined;
    const group = findGroupByInstanceId(this.self.actionGroups, instanceId);
    if (group?.config.oneofKind !== "loop") return undefined;

    const block = this.buildDefaultBlock();
    group.config.loop.actionGroups = [...group.config.loop.actionGroups, block];
    return block.instanceId;
  }

  /** Inserts a nested Looping block into the loop's sequence (spec 6.3). */
  addLoopInLoop(instanceId: number): number | undefined {
    if (!this.canAddLoopInLoop(instanceId)) return undefined;
    const group = findGroupByInstanceId(this.self.actionGroups, instanceId);
    if (group?.config.oneofKind !== "loop") return undefined;

    const loop = this.buildDefaultLoop();
    group.config.loop.actionGroups = [...group.config.loop.actionGroups, loop];
    return loop.instanceId;
  }

  /**
   * True when the loop holds any content: actions (deep) or any nested
   * non-actions group -- drives the delete-confirmation rule, mirroring
   * branchHasContent.
   */
  loopHasContent(instanceId: number): boolean {
    const group = findGroupByInstanceId(this.self.actionGroups, instanceId);
    if (group?.config.oneofKind !== "loop") return false;

    const hasNestedGroups = group.config.loop.actionGroups.some(
      (nested) => nested.config.oneofKind !== "actions",
    );
    return (
      hasNestedGroups ||
      collectActionsDeep(group.config.loop.actionGroups).length > 0
    );
  }

  /**
   * Direct actions of every container on the ancestor path to the group
   * (root actions groups, then each ancestor branch's/loop's own direct
   * actions, in path order). These are the actions that execute BEFORE the
   * group -- the valid upstream candidates for a nested loop's record-list
   * source. Deliberately excludes: sibling branches (only one branch runs
   * at a time, so a sibling's actions aren't guaranteed to have executed),
   * and anything nested INSIDE the target group itself. Empty array when
   * the group is not found.
   */
  upstreamActionsOf(instanceId: number): ActionCandidate[] {
    const result: ActionCandidate[] = [];
    const found = this.collectUpstreamActions(
      this.self.actionGroups,
      instanceId,
      result,
    );
    return found ? result : [];
  }

  /** All actions on the execution path BEFORE the given action: direct actions
   * of every ancestor container up to the action's own group, plus the actions
   * preceding it within that group. Sibling branches/loops never leak
   * (checkpoint/rollback, mirroring collectUpstreamActions). */
  upstreamActionsOfAction(actionHash: string): ActionCandidate[] {
    const result: ActionCandidate[] = [];
    const found = this.collectUpstreamActionsForAction(
      this.self.actionGroups,
      actionHash,
      result,
    );
    return found ? result : [];
  }

  /** Appends a new branches action group to the root action groups; returns its instanceId. */
  addBlock(): number {
    const group = this.buildDefaultBlock();
    this.self.actionGroups = [...this.self.actionGroups, group];
    return group.instanceId;
  }

  /** Removes a branches block anywhere in the tree (root or nested in a
   * branch/loop) and ejects references to its actions. */
  deleteBlock(instanceId: number): void {
    const blockGroup = this.findBlockGroup(instanceId);
    if (blockGroup === undefined) return;

    const deletedActions = collectActionsDeep([blockGroup]);
    this.removeGroupFromTree(instanceId);
    this.ejectDeletedActionReferences(deletedActions);
  }

  /** Removes a loop group (root-level or nested inside a branch) and ejects
   * references to its actions -- `removeGroupFromTree`'s root fast-path
   * covers both cases. */
  deleteNestedLoop(instanceId: number): boolean {
    const group = findGroupByInstanceId(this.self.actionGroups, instanceId);
    if (group === undefined || group.config.oneofKind !== "loop") return false;

    const deletedActions = collectActionsDeep([group]);
    this.removeGroupFromTree(instanceId);
    this.ejectDeletedActionReferences(deletedActions);
    return true;
  }

  setBlockLabel(instanceId: number, label: string): void {
    const block = this.findBlockGroup(instanceId);
    if (block === undefined) return;

    block.config.branches.label = label;
  }

  /**
   * Adds a new conditions branch, appended after the existing branches
   * (i.e. before the else-branch slot, which lives outside `branches[]`).
   * Guard: `maxConditionsBranches` per block, counting the else-branch (spec
   * 2.3 -- the else-branch is the block's last branch, so it counts toward the
   * cap; otherwise 10 conditions + else = 11 slips through).
   * Returns the new branch's hash so callers can select it without relying
   * on the append-at-end position, or undefined when nothing was added.
   */
  addBranch(instanceId: number): string | undefined {
    const block = this.findBlock(instanceId);
    if (block === undefined) return undefined;
    const totalBranches =
      block.branches.length + (block.elseBranch !== undefined ? 1 : 0);
    if (totalBranches >= this.limits.maxConditionsBranches) return undefined;

    const branch: BranchCandidate = {
      hash: uuid(),
      label: this.nextBranchLabel(block),
      conditions: {
        operator: ConditionsLogicOperator.OPERATOR_AND,
        group: [],
      },
      actionGroups: [
        {
          instanceId: this.nextInstanceId(),
          config: { oneofKind: "actions", actions: { actions: [] } },
        },
      ],
    };

    block.branches = [...block.branches, branch];
    return branch.hash;
  }

  /**
   * Deletes a branch (a conditions branch, or the else-branch).
   * Guard: the combined conditions+else total must stay >= `minTotalBranches`
   * -- i.e. the last remaining branch in the group, whichever kind it is,
   * can't be deleted.
   */
  deleteBranch(ref: BranchRef): boolean {
    const block = this.findBlock(ref.instanceId);
    if (block === undefined) return false;

    if (ref.branchHash === "else") {
      if (block.elseBranch === undefined) return false;
      if (block.branches.length < this.limits.minTotalBranches) return false;

      const deletedActions = collectActionsDeep(block.elseBranch.actionGroups);
      block.elseBranch = undefined;
      this.ejectDeletedActionReferences(deletedActions);
      return true;
    }

    const branch = block.branches.find((b) => b.hash === ref.branchHash);
    if (branch === undefined) return false;

    const totalAfter =
      block.branches.length - 1 + (block.elseBranch !== undefined ? 1 : 0);
    if (totalAfter < this.limits.minTotalBranches) return false;

    const deletedActions = collectActionsDeep(branch.actionGroups);
    block.branches = block.branches.filter((b) => b.hash !== ref.branchHash);
    this.ejectDeletedActionReferences(deletedActions);
    return true;
  }

  setBranchLabel(ref: BranchRef, label: string): void {
    const branch = this.findBranch(ref);
    if (branch === undefined) return;

    branch.label = label;
  }

  /**
   * Switches a branch between "conditions" and "else" mode.
   * - "else": only allowed while the block has no else-branch yet; drops
   *   the branch's conditions and moves it into the elseBranch slot,
   *   keeping hash/label/actionGroups.
   * - "conditions": only allowed via the "else" ref; appends the
   *   else-branch to the end of `branches[]` with a fresh, empty
   *   AND-conditions group, keeping hash/label/actionGroups.
   */
  setBranchMode(ref: BranchRef, mode: "conditions" | "else"): boolean {
    const block = this.findBlock(ref.instanceId);
    if (block === undefined) return false;

    if (mode === "else") {
      if (ref.branchHash === "else") return false;
      if (block.elseBranch !== undefined) return false;

      const branch = block.branches.find((b) => b.hash === ref.branchHash);
      if (branch === undefined) return false;
      // Converting one conditions branch into the else-branch never changes
      // the combined total, so this is a defensive invariant check rather
      // than a real gate: with the floor at 1, it's unreachable in practice
      // (an empty block.branches would have already failed the `find` above).
      if (block.branches.length < this.limits.minTotalBranches) return false;

      block.branches = block.branches.filter((b) => b.hash !== ref.branchHash);
      block.elseBranch = {
        hash: branch.hash,
        label: branch.label,
        actionGroups: branch.actionGroups,
      };
      return true;
    }

    if (ref.branchHash !== "else") return false;
    if (block.elseBranch === undefined) return false;
    if (block.branches.length >= this.limits.maxConditionsBranches)
      return false;

    const elseBranch = block.elseBranch;
    block.branches = [
      ...block.branches,
      {
        hash: elseBranch.hash,
        label: elseBranch.label,
        conditions: {
          operator: ConditionsLogicOperator.OPERATOR_AND,
          group: [],
        },
        actionGroups: elseBranch.actionGroups,
      },
    ];
    block.elseBranch = undefined;
    return true;
  }

  addCondition(ref: BranchRef): void {
    const branch = this.findConditionsBranch(ref);
    if (branch === undefined) return;

    // Guard: max 15 conditions per branch (spec 3.5), matching the existing
    // trigger-condition limit. The UI Add button is also disabled at the cap,
    // but the store enforces it so no caller can exceed it.
    if (
      (branch.conditions?.group.length ?? 0) >= this.limits.maxTriggerConditions
    ) {
      return;
    }

    // Branch conditions are reference-based (the left side stores an
    // outputReference, not the trigger's field ids), so every new condition --
    // including the first -- starts EMPTY. Seeding the first one from the
    // trigger's default (getDefaultConditionsGroup: field-status + a default
    // function, which can be "changes") wrongly rendered the "changes" FROM/TO
    // editor in a branch, where "changes" is never valid (spec 3.4).
    const newGroup: ConditionsConfig_ConditionsConfigGroup = {
      group: {
        oneofKind: "condition",
        condition: { conditionFieldId: "" },
      },
    };

    branch.conditions = {
      operator:
        branch.conditions?.operator ?? ConditionsLogicOperator.OPERATOR_AND,
      group: [...(branch.conditions?.group ?? []), newGroup],
    };
  }

  updateCondition(
    ref: BranchRef,
    condition: ConditionsConfig_ConditionConfig,
    index: number,
  ): void {
    const branch = this.findConditionsBranch(ref);
    if (branch === undefined || branch.conditions === undefined) return;

    branch.conditions.group = branch.conditions.group.map((item, i) =>
      i === index
        ? {
            group: {
              oneofKind: "condition",
              condition,
            },
          }
        : item,
    );
  }

  deleteCondition(ref: BranchRef, index: number): void {
    const branch = this.findConditionsBranch(ref);
    if (branch === undefined || branch.conditions === undefined) return;

    branch.conditions.group = branch.conditions.group.filter(
      (_, i) => i !== index,
    );
  }

  /**
   * Resets every branch condition whose left side references the trigger to
   * an empty condition (the shape addCondition seeds). Called on trigger
   * change/table change, when the previous trigger's outputs are gone --
   * the branch-condition counterpart of looping.clearSourceIfTriggerReference
   * (BUG-21229).
   */
  clearConditionsIfTriggerReference(): void {
    this.clearConditionsMatching(
      (reference) => reference.source.oneofKind === "triggerReference",
    );
  }

  /**
   * Same as clearConditionsIfTriggerReference, but for conditions whose left
   * side references the given action instance -- called when that action's
   * type is changed (the instanceId survives the change, so stale references
   * would otherwise keep resolving against the replacement action).
   */
  clearConditionsIfActionReference(actionReference: ActionReference): void {
    this.clearConditionsMatching(
      (reference) =>
        reference.source.oneofKind === "actionReference" &&
        reference.source.actionReference.instanceId ===
          actionReference.instanceId,
    );
  }

  switchConditionOperator(ref: BranchRef): void {
    const branch = this.findConditionsBranch(ref);
    if (branch === undefined || branch.conditions === undefined) return;

    branch.conditions.operator =
      branch.conditions.operator === ConditionsLogicOperator.OPERATOR_AND
        ? ConditionsLogicOperator.OPERATOR_OR
        : ConditionsLogicOperator.OPERATOR_AND;
  }

  setBranchActions(ref: BranchRef, ...mergers: ActionListMerger[]): void {
    this.candidateStore.setActionsAt(
      {
        kind: "branch",
        instanceId: ref.instanceId,
        branchHash: ref.branchHash,
      },
      mergers,
    );
  }

  /** Deep lookup of the branches action group itself (with instanceId), anywhere in the tree. */
  private findBlockGroup(instanceId: number): BranchesActionGroup | undefined {
    const group = findGroupByInstanceId(this.self.actionGroups, instanceId);
    return group !== undefined && group.config.oneofKind === "branches"
      ? (group as BranchesActionGroup)
      : undefined;
  }

  /** Filters the group out of whichever container holds it: the root list, a
   * branch's/else-branch's actionGroups, or a loop's actionGroups. */
  private removeGroupFromTree(instanceId: number): void {
    const rootHit = this.self.actionGroups.some(
      (group) => group.instanceId === instanceId,
    );
    if (rootHit) {
      this.self.actionGroups = this.self.actionGroups.filter(
        (group) => group.instanceId !== instanceId,
      );
      return;
    }

    walkActionGroups(this.self.actionGroups, (group) => {
      if (group.config.oneofKind === "loop") {
        group.config.loop.actionGroups = group.config.loop.actionGroups.filter(
          (nested) => nested.instanceId !== instanceId,
        );
      }
      if (group.config.oneofKind === "branches") {
        group.config.branches.branches.forEach((branch) => {
          branch.actionGroups = branch.actionGroups.filter(
            (nested) => nested.instanceId !== instanceId,
          );
        });
        const elseBranch = group.config.branches.elseBranch;
        if (elseBranch !== undefined) {
          elseBranch.actionGroups = elseBranch.actionGroups.filter(
            (nested) => nested.instanceId !== instanceId,
          );
        }
      }
    });
  }

  /**
   * Default label for a new branch: the smallest "Branch <letter>" not
   * already taken within the block (branches + else, trimmed). Branches are
   * named alphabetically (Branch A, Branch B, Branch C, ...) to stay
   * consistent with the two default branches. Stepping past taken labels
   * avoids duplicates after an else-mode round trip (add -> switch to else
   * -> add again), which would otherwise trip the blocking branch-name
   * validation without the user typing anything.
   */
  private nextBranchLabel(block: BranchesActionGroupCandidate): string {
    const taken = new Set(
      [
        ...block.branches.map((branch) => branch.label),
        ...(block.elseBranch !== undefined ? [block.elseBranch.label] : []),
      ].map((label) => label.trim()),
    );
    let index = block.branches.length;
    let label = this.branchLabelForIndex(index);
    while (taken.has(label.trim())) {
      index += 1;
      label = this.branchLabelForIndex(index);
    }
    return label;
  }

  /**
   * Alphabetical branch label for a 0-based position: 0 -> "Branch A",
   * 1 -> "Branch B", ... 25 -> "Branch Z", 26 -> "Branch AA" (Excel-style
   * columns, so it never runs out even though the 10-branch cap keeps it to
   * single letters in practice).
   */
  private branchLabelForIndex(index: number): string {
    let n = index;
    let letters = "";
    do {
      letters = String.fromCharCode(65 + (n % 26)) + letters;
      n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return String(t("branches.default-branch", { letter: letters }));
  }

  /** findBranch narrowed to a conditions branch; undefined for the else-branch ref. */
  private findConditionsBranch(ref: BranchRef): BranchCandidate | undefined {
    if (ref.branchHash === "else") return undefined;
    return this.findBranch(ref) as BranchCandidate | undefined;
  }

  /**
   * DFS to the branch identified by ref, accumulating per-type counts of the
   * advanced groups on the path INCLUDING the branch's own block. Returns
   * true when the branch was found (counts are then valid).
   */
  private countAncestors(
    groups: ActionGroupCandidate[],
    ref: BranchRef,
    counts: { branches: number; loop: number },
  ): boolean {
    for (const group of groups) {
      if (group.config.oneofKind === "loop") {
        counts.loop += 1;
        if (this.countAncestors(group.config.loop.actionGroups, ref, counts)) {
          return true;
        }
        counts.loop -= 1;
      }

      if (group.config.oneofKind === "branches") {
        counts.branches += 1;
        if (group.instanceId === ref.instanceId) {
          const exists =
            ref.branchHash === "else"
              ? group.config.branches.elseBranch !== undefined
              : group.config.branches.branches.some(
                  (branch) => branch.hash === ref.branchHash,
                );
          if (exists) return true;
        }
        for (const branch of group.config.branches.branches) {
          if (this.countAncestors(branch.actionGroups, ref, counts)) {
            return true;
          }
        }
        const elseBranch = group.config.branches.elseBranch;
        if (
          elseBranch !== undefined &&
          this.countAncestors(elseBranch.actionGroups, ref, counts)
        ) {
          return true;
        }
        counts.branches -= 1;
      }
    }
    return false;
  }

  /**
   * DFS to the group identified by instanceId, accumulating per-type counts
   * of advanced groups on the path INCLUDING the target itself when it is an
   * advanced group. Returns true when found.
   */
  private countAncestorsOfGroup(
    groups: ActionGroupCandidate[],
    instanceId: number,
    counts: { branches: number; loop: number },
  ): boolean {
    for (const group of groups) {
      if (group.config.oneofKind === "loop") {
        counts.loop += 1;
        if (group.instanceId === instanceId) return true;
        if (
          this.countAncestorsOfGroup(
            group.config.loop.actionGroups,
            instanceId,
            counts,
          )
        ) {
          return true;
        }
        counts.loop -= 1;
      }

      if (group.config.oneofKind === "branches") {
        counts.branches += 1;
        if (group.instanceId === instanceId) return true;
        for (const branch of group.config.branches.branches) {
          if (
            this.countAncestorsOfGroup(branch.actionGroups, instanceId, counts)
          )
            return true;
        }
        const elseBranch = group.config.branches.elseBranch;
        if (
          elseBranch !== undefined &&
          this.countAncestorsOfGroup(
            elseBranch.actionGroups,
            instanceId,
            counts,
          )
        ) {
          return true;
        }
        counts.branches -= 1;
      }

      if (
        group.config.oneofKind === "actions" &&
        group.instanceId === instanceId
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * DFS mirroring countAncestorsOfGroup's structure, but accumulating direct
   * actions of every container passed on the way to instanceId instead of
   * counting advanced-group ancestors. Returns true (and leaves `result`
   * populated) once the target is found; stops descending into a container
   * as soon as the target is located inside it, so nothing nested inside
   * the target itself -- nor anything that executes after it -- is added.
   *
   * Invariant, mirroring countAncestorsOfGroup's decrement-on-backtrack
   * pattern: on a `false` return, `result` is left exactly as it was on
   * entry. A subtree (a loop's contents, or one branch's contents) may push
   * onto `result` while searching and still not contain the target -- e.g.
   * the first of two sibling branches with its own direct actions, while
   * the target sits inside the second branch. Those speculative pushes
   * must be rolled back before trying the next sibling, otherwise a
   * sibling's (or a failed loop's) actions leak into the result even
   * though only one branch actually runs and siblings never precede the
   * target on any real execution path.
   */
  private collectUpstreamActions(
    groups: ActionGroupCandidate[],
    instanceId: number,
    result: ActionCandidate[],
  ): boolean {
    for (const group of groups) {
      if (group.instanceId === instanceId) return true;

      if (group.config.oneofKind === "actions") {
        result.push(...group.config.actions.actions);
        continue;
      }

      if (group.config.oneofKind === "loop") {
        const checkpoint = result.length;
        if (
          this.collectUpstreamActions(
            group.config.loop.actionGroups,
            instanceId,
            result,
          )
        ) {
          return true;
        }
        result.length = checkpoint;
        continue;
      }

      if (group.config.oneofKind === "branches") {
        for (const branch of group.config.branches.branches) {
          const checkpoint = result.length;
          if (
            this.collectUpstreamActions(branch.actionGroups, instanceId, result)
          ) {
            return true;
          }
          result.length = checkpoint;
        }
        const elseBranch = group.config.branches.elseBranch;
        if (elseBranch !== undefined) {
          const checkpoint = result.length;
          if (
            this.collectUpstreamActions(
              elseBranch.actionGroups,
              instanceId,
              result,
            )
          ) {
            return true;
          }
          result.length = checkpoint;
        }
      }
    }
    return false;
  }

  /**
   * Same DFS/checkpoint-rollback shape as collectUpstreamActions, but keyed
   * by action hash rather than group instanceId: when an "actions" group
   * contains the target action, only the actions BEFORE it in that group
   * are pushed (the rest of that group, and anything after it anywhere,
   * never executes before the target). Sibling branches and a failed loop's
   * speculative pushes are rolled back exactly as in collectUpstreamActions.
   */
  private collectUpstreamActionsForAction(
    groups: ActionGroupCandidate[],
    actionHash: string,
    result: ActionCandidate[],
  ): boolean {
    for (const group of groups) {
      if (group.config.oneofKind === "actions") {
        const idx = group.config.actions.actions.findIndex(
          (action) => action.hash === actionHash,
        );
        if (idx !== -1) {
          result.push(...group.config.actions.actions.slice(0, idx));
          return true;
        }
        result.push(...group.config.actions.actions);
        continue;
      }

      if (group.config.oneofKind === "loop") {
        const checkpoint = result.length;
        if (
          this.collectUpstreamActionsForAction(
            group.config.loop.actionGroups,
            actionHash,
            result,
          )
        ) {
          return true;
        }
        result.length = checkpoint;
        continue;
      }

      if (group.config.oneofKind === "branches") {
        for (const branch of group.config.branches.branches) {
          const checkpoint = result.length;
          if (
            this.collectUpstreamActionsForAction(
              branch.actionGroups,
              actionHash,
              result,
            )
          ) {
            return true;
          }
          result.length = checkpoint;
        }
        const elseBranch = group.config.branches.elseBranch;
        if (elseBranch !== undefined) {
          const checkpoint = result.length;
          if (
            this.collectUpstreamActionsForAction(
              elseBranch.actionGroups,
              actionHash,
              result,
            )
          ) {
            return true;
          }
          result.length = checkpoint;
        }
      }
    }
    return false;
  }

  /**
   * Builds a fresh branches action group with 2 default conditions
   * branches. This is the single source of the default block shape --
   * `addBlock()` is its only caller today, but any future code that needs
   * a fresh default block should call this rather than hand-rolling the
   * shape again.
   */
  private buildDefaultBlock(): ActionGroupCandidate {
    const instanceId = this.nextInstanceId();
    return {
      instanceId,
      config: {
        oneofKind: "branches",
        branches: {
          label: String(t("branches.default-block-name")),
          branches: [
            this.buildEmptyBranch(this.branchLabelForIndex(0), instanceId + 1),
            this.buildEmptyBranch(this.branchLabelForIndex(1), instanceId + 2),
          ],
        },
      },
    };
  }

  /** Builds an empty Looping group with a single empty actions group. Mirrors
   * the loop shape minted by addLoopAt / mergers addLoopActionGroup. */
  private buildDefaultLoop(): ActionGroupCandidate {
    const instanceId = this.nextInstanceId();
    return {
      instanceId,
      config: {
        oneofKind: "loop",
        loop: {
          name: "",
          actionGroups: [
            {
              instanceId: instanceId + 1,
              config: { oneofKind: "actions", actions: { actions: [] } },
            },
          ],
        },
      },
    };
  }

  private buildEmptyBranch(label: string, instanceId: number): BranchCandidate {
    return {
      hash: uuid(),
      label,
      conditions: { operator: ConditionsLogicOperator.OPERATOR_AND, group: [] },
      actionGroups: [
        {
          instanceId,
          config: { oneofKind: "actions", actions: { actions: [] } },
        },
      ],
    };
  }

  private nextInstanceId(): number {
    const allInstanceIds = collectInstanceIdsDeep(this.self.actionGroups);
    return Math.max(-1, ...allInstanceIds) + 1;
  }

  /**
   * Removes references to the given deleted actions from every remaining
   * action in the tree (inputs, smartdoc/checklist pills) -- the deep
   * counterpart of setActionsWithOutReferences, which only walks the root
   * group. Reuses ejectReferences, the same primitive ordinary
   * action-deletion goes through.
   */
  private ejectDeletedActionReferences(
    deletedActions: ActionCandidate[],
  ): void {
    const references = deletedActions
      .map((action) => action.actionReference)
      .filter((ref): ref is ActionReference => ref !== undefined);
    if (references.length === 0) return;

    this.self.actionGroups = references.reduce(
      (groups, reference) =>
        mapActionsDeep(groups, (action) =>
          ejectReferences(action, undefined, reference),
        ),
      this.self.actionGroups,
    );
  }

  /** Walks every branch in the tree and resets matching reference-mode conditions to empty. */
  private clearConditionsMatching(
    matches: (reference: OutputReference) => boolean,
  ): void {
    walkActionGroups(this.self.actionGroups, (group) => {
      if (group.config.oneofKind !== "branches") return;
      for (const branch of group.config.branches.branches) {
        if (branch.conditions === undefined) continue;
        branch.conditions.group = branch.conditions.group.map((item) =>
          item.group.oneofKind === "condition" &&
          item.group.condition.outputReference !== undefined &&
          matches(item.group.condition.outputReference)
            ? {
                group: {
                  oneofKind: "condition",
                  condition: { conditionFieldId: "" },
                },
              }
            : item,
        );
      }
    });
  }
}
